import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import * as api from "../../api";
import {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {useFeedbackTools} from "../../components/FeedbackTools";
import {useNavigate} from "react-router-dom";


export const useModel23Query = (model23Id, options={}) => {
    return useQuery({
        queryKey: ['models23', model23Id],
        queryFn: () => api.Model23.getModel23(model23Id),
        ...options
    });

}

export const useModels23Query = (models23Filters={}, options={}) => {
    const {prefetch=true} = options;

    const query = useQuery({
        queryKey: ['models23', models23Filters],
        queryFn: () => api.Model23.getModels23(models23Filters),
        keepPreviousData: true
    });

    // Prefetch the next page!
    const queryClient = useQueryClient()
    const {isPreviousData} = query;
    const hasMoreData = query.data?.page_count > models23Filters.pageIndex + 1;

    // note that models23Filters should be memorized to avoid useless prefetch
    useEffect(() => {
        if(!prefetch){
            return;
        }
        if (!isPreviousData && hasMoreData) {
            const nextPageFilter = {...models23Filters, pageIndex: models23Filters.pageIndex + 1}
            queryClient.prefetchQuery({
                queryKey: ['models23', nextPageFilter],
                queryFn: () => api.Model23.getModels23(nextPageFilter),
            })
        }
    }, [isPreviousData, hasMoreData, models23Filters, queryClient, prefetch])

    return query;
}

export const useModel23RegistryQuery = () => {
    const {i18n} = useTranslation();
    const locale = i18n.language;

    return useQuery({
        queryKey: ['registry', 'model23', locale],
        queryFn: () => api.Registry.getModel23Registry(locale),
        keepPreviousData: true,
        staleTime: 1000 * 60 * 60 * 24 * 7, // 1 week
    });
}

export const useCreateModel23 = () => {
    const {t} = useTranslation(["models23"])
    const queryClient = useQueryClient();
    const {showSuccessNotification}  = useFeedbackTools();

    return useMutation({
        mutationFn: async (data) => await api.Model23.createModel23(data),
        onSuccess: () => {
            queryClient.invalidateQueries(['models23']);
            queryClient.invalidateQueries(['geo', 'model23Stations']);
            showSuccessNotification(t('query.createSuccess'));
        }
    });
}

export const useUpdateModel23 = (model23Id) => {
    const {t} = useTranslation(["models23"])
    const queryClient = useQueryClient();
    const {showSuccessNotification}  = useFeedbackTools();

    return useMutation({
        mutationFn: async (data) => await api.Model23.updateModel23(model23Id, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['models23']);
            queryClient.invalidateQueries(['geo', 'model23Stations']);
            showSuccessNotification(t('query.updateSuccess'));
        }
    });
}

export const useModel23ExportMutation = () => {
    return useMutation({
        mutationFn: async ({id, format}) => await api.Model23.exportModel23(id, format),
        onSuccess: (data, ) => {
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(new Blob([data.data]));
            link.download = data.fileName; // specify the filename
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
    });
}


export const useModel23DeleteMutation = () => {
    const {t} = useTranslation(["models23"])
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const {showSuccessNotification}  = useFeedbackTools();

    return useMutation({
        mutationFn: async (id) => await api.Model23.deleteModel23(id),
        onSuccess: async (data) => {
            await navigate('/models23');
            showSuccessNotification(t('query.deleteSuccess'));
            queryClient.invalidateQueries(['models23']);
            queryClient.invalidateQueries(['geo', 'model23Stations']);
        }
    });
}


export const useModels23ExportMutation = () => {
    return useMutation({
        mutationFn: async (data) => await api.Model23.exportModels23(data),
        onSuccess: (data, ) => {
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(new Blob([data.data]));
            link.download = data.fileName; // specify the filename
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
    });
}


export const useCreateModel23ExportTask = () => {
    return useMutation({
        mutationFn: async ({workgroupId, filters, data}) => await api.ExportTask.createModel23ExportTask(workgroupId, filters, data),
    });
}